import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { getTerms } from '../../lib/api/user/userApi';

const TermsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SectionContainer = styled.div`
  background-color: #ffffff;
  padding: 24px 0;
`;

const WebSectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  text-align: left;
  
  @media (max-width: 767px) {
    display: none;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 24px;
`;

const TabItem = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 16px 0;
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.$isActive ? '#69b5b0' : '#666'};
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.$isActive ? '#69b5b0' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: pre-line;
  text-align: center;
  
  &:hover {
    color: #69b5b0;
  }
`;

const ContentContainer = styled.div`
  min-height: 400px;
  line-height: 1.6;
  
  /* 웹 에디터 컨텐츠 스타일링 */
  p {
    margin-bottom: 16px;
    color: #333;
    font-size: 14px;
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin: 24px 0 16px 0;
    color: #333;
    font-weight: 600;
  }
  
  ul, ol {
    margin: 16px 0;
    padding-left: 24px;
  }
  
  li {
    margin-bottom: 8px;
    color: #333;
    font-size: 14px;
  }
  
  strong {
    font-weight: 600;
    color: #333;
  }
  
  em {
    font-style: italic;
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
  }
  
  th, td {
    border: 1px solid #ddd;
    padding: 12px;
    text-align: left;
    font-size: 14px;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #666;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #999;
  font-size: 16px;
`;

interface TermsData {
  key: string;
  title: string;
  content: string;
}

// 다국어 텍스트 객체
const texts = {
  ko: {
    termsView: '약관보기',
    terms: '약관',
    privacyPolicy: '개인정보\n취급방침',
    serviceTerms: '서비스\n이용약관',
    businessInfo: '사업자\n정보',
    loading: '약관을 불러오는 중...',
    loadError: '약관을 불러올 수 없습니다.',
    termsLoadFailed: '약관을 불러오는데 실패했습니다.'
  },
  en: {
    termsView: 'Terms',
    terms: 'Terms',
    privacyPolicy: 'Privacy\nPolicy',
    serviceTerms: 'Terms of\nService',
    businessInfo: 'Business\nInformation',
    loading: 'Loading terms...',
    loadError: 'Unable to load terms.',
    termsLoadFailed: 'Failed to load terms.'
  },
};

function TermsView() {
  const navigate = useNavigate();
  const { lang } = useLang();

  const t = texts[lang];
  
  const [termsData, setTermsData] = useState<TermsData[]>([]);
  const [activeTab, setActiveTab] = useState('privacy');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // 약관 데이터 불러오기
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setIsLoading(true);
        setError(false);
        
        const termLang = lang === 'ko' ? 'KOR' : 'ENG';
        const response = await getTerms({ termLang });
        
        if (response.data.statusCode === 200 && response.data.data) {
          const termsArray = response.data.data as any[];
          
          // 언어에 따라 인덱스 매핑
          let privacyIndex, serviceIndex, businessIndex;
          if (lang === 'ko') {
            // KOR: 1(privacy), 3(service), 5(business)
            privacyIndex = termsArray.find((term: any) => term.index === 1);
            serviceIndex = termsArray.find((term: any) => term.index === 3);
            businessIndex = termsArray.find((term: any) => term.index === 5);
          } else {
            // ENG: 2(privacy), 4(service), 6(business)
            privacyIndex = termsArray.find((term: any) => term.index === 2);
            serviceIndex = termsArray.find((term: any) => term.index === 4);
            businessIndex = termsArray.find((term: any) => term.index === 6);
          }

          const formattedTerms: TermsData[] = [
            {
              key: 'privacy',
              title: t.privacyPolicy,
              content: privacyIndex?.content || ''
            },
            {
              key: 'service',
              title: t.serviceTerms,
              content: serviceIndex?.content || ''
            },
            {
              key: 'business',
              title: t.businessInfo,
              content: businessIndex?.content || ''
            }
          ];

          setTermsData(formattedTerms);
          
          // 첫 번째 탭을 기본 활성화
          if (formattedTerms.length > 0) {
            setActiveTab(formattedTerms[0].key);
          }
        } else {
          setError(true);
        }
      } catch (error) {
        devError('약관 조회 실패:', error);
        toast.error(t.termsLoadFailed);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTerms();
  }, [lang, t.termsLoadFailed, t.privacyPolicy, t.serviceTerms, t.businessInfo]);

  const activeTerms = termsData.find(term => term.key === activeTab);

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          {t.loading}
        </LoadingContainer>
      );
    }

    if (error || !activeTerms) {
      return (
        <ErrorContainer>
          {t.loadError}
        </ErrorContainer>
      );
    }

    return (
      <ContentContainer 
        dangerouslySetInnerHTML={{ __html: activeTerms.content }}
      />
    );
  };

  return (
    <MainLayout>
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="mypage"
          title={t.termsView}
          showBackButton={true}
          onBack={() => navigate('/mypage')}
        />
        <TermsContainer>
          <SectionContainer>
            <WebSectionTitle>{t.terms}</WebSectionTitle>
            
            {termsData.length > 0 && (
              <TabContainer>
                {termsData.map((term) => (
                  <TabItem
                    key={term.key}
                    $isActive={activeTab === term.key}
                    onClick={() => setActiveTab(term.key)}
                  >
                    {term.title}
                  </TabItem>
                ))}
              </TabContainer>
            )}

            {renderContent()}
          </SectionContainer>
        </TermsContainer>
      </CenteredLayout>
    </MainLayout>
  );
}

export default TermsView;
